import { DownOutlined, PlusOutlined } from "@ant-design/icons";
import { Badge, Button, Dropdown, Menu, Modal, Tag } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import { rightsService } from "../../services/rightsService";
import { API_URL, TOKEN } from "../../variables/constants";
import { toast } from "react-toastify";
import axiosInstance from "../../variables/api/axiosInstance";
import { highlightMatch } from "../../variables/stringFunctions";
import { searchProfil } from "../../hooks/useSearch";
import ExportableFilterableTable from "../../components/tables/ExportableFilterableTable";

const Profils = () => {
  const [profils, setProfils] = useState([]);
  const [allProfils, setAllProfils] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, showSizeChanger: true });
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  const navigate = useNavigate();

  const fetchData = (params = {}) => {
    setLoading(true);
    axiosInstance
      .get(`/api/profil`, {
        params: {
          page: params.pagination.current - 1,
          size: params.pagination.pageSize,
          ...params.filters,
        },
      })
      .then((res) => {
        setPagination({
          ...params.pagination,
          total: res.data?.totalElements,
        });
        console.log(res.data);
        const flattened = (res.data?.content || []).map((p) => {
          const habilitationNames = (p?.habilitations || [])
            .map((h) => h?.libelle || h?.nom || h?.codeHabilitation || h?.code)
            .filter(Boolean);

          return {
            ...p,
            organismeName: p.organisme?.raisonSociale,
            habilitationNames,
          };
        });
        setProfils(flattened);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }

  const fetchAllForExport = () => {
    axiosInstance
      .get(`/api/profil`, {
        params: {
          page: 0,
          size: 10000,
        },
      })
      .then((res) => {
        const flattened = (res.data?.content || []).map((p) => {
          const habilitationNames = (p?.habilitations || [])
            .map((h) => h?.libelle || h?.nom || h?.codeHabilitation || h?.code)
            .filter(Boolean);

          return {
            ...p,
            organismeName: p.organisme?.raisonSociale,
            habilitationNames,
          };
        });
        setAllProfils(flattened);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    fetchData({ pagination })
    fetchAllForExport();
  }, [currentPage, pageSize]);

  const handleChange = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    setPagination(pagination);
  };

  const handleDelete = (profil) => {
    Modal.confirm({
      title: "Confirmation de suppression",
      content: `Êtes-vous sûr de vouloir supprimer le profil "${profil.nom}" ?`,
      okText: "Oui",
      okType: "danger",
      cancelText: "Non",
      onOk: () => {
        setLoading(true);
        return axios
          .delete(`${API_URL}/api/profil`, {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
              "Content-Type": "application/json",
            },
            data: profil, // Envoyer le profil à supprimer dans le body
          })
          .then(() => {
            toast("Profil supprimé avec succès !");
            fetchData({ pagination });
            fetchAllForExport();
          })
          .catch((err) => {
            console.error(err);
            const apiMessage =
              err?.response?.data?.message ||
              err?.response?.data?.error ||
              (typeof err?.response?.data === "string" ? err.response.data : "");
            const lowerMessage = apiMessage?.toLowerCase?.() || "";
            if (lowerMessage.includes("impossible de supprimer ce profil")) {
              toast(apiMessage);
            } else {
              toast("Une erreur est survenue lors de la suppression.");
            }
          })
          .finally(() => {
            setLoading(false);
          });
      },
    });
  };

  const organismFilterOptions = Array.from(
    new Set(profils.map((p) => p.organismeName).filter(Boolean))
  ).map((name) => ({ label: name, value: name }));

  const columns = [
    {
      title: "Intitulé",
      dataIndex: "nom",
      key: "nom",
      searchable: true,
    },
    {
      title: "Organisme",
      dataIndex: "organismeName",
      key: "organismeName",
      searchable: true,
      filterType: "select",
      filterOptions: organismFilterOptions,
      render: (_, record) => <Badge color="blue" text={record.organismeName} />,
    },
    {
      title: "Habilitations",
      dataIndex: "habilitationNames",
      key: "habilitations",
      hideInTable: true,
      exportValue: (row) => (row.habilitationNames || []).join(" | "),
    },
    {
      title: "Actions",
      key: "action",
      hideInExport: true,
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              {rightsService("HAC056") && (
                <Menu.Item key="1">
                  <a href={`/modifier-profil/${record.codeId}`}></a> Modifier
                </Menu.Item>
              )}
              {rightsService("HAC058") && (
                <Menu.Item key="2" onClick={() => handleDelete(record)}>
                  Supprimer
                </Menu.Item>
              )}
            </Menu>
          }
        >
          <Button icon={<DownOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const renderSearchResults = (results) => {
    return <div className="px-3 py-2">
      {results.map((item, index) => (
        <div
          key={index}
          onClick={() =>
            navigate(`/modifier-profil/${item.codeId}`)
          }
          className="dropdown-item d-flex align-items-center gap-2 py-1"
          style={{ cursor: "pointer" }}
        >
          <span className="text-truncate">
            {[item?.nom, item?.organisme?.raisonSociale].map(
              (text, i) => (
                <React.Fragment key={i}>
                  {i > 0 && " - "}
                  {highlightMatch(text?.toString(), searchValue)}
                </React.Fragment>
              )
            )}
          </span>
        </div>
      ))}
    </div>;
  }

  useEffect(() => {
    if (searchValue && searchValue?.length > 2) {
      searchProfil(searchValue).then((res) => {
        setSearchResults(res.data.content);
      }).catch((err) => {
        console.log(err);
      });
    }
  }, [searchValue]);

  return (
    <div class="layout-wrapper layout-content-navbar  ">
      <div class="layout-container">
        <SideBar currentMenu={"PROFILS"} />

        {/* <!-- Layout container --> */}
        <div class="layout-page">
          <NavBar
            filteredResults={searchResults}
            setFilteredResults={setSearchResults}
            renderSearchResults={renderSearchResults}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />

          <div class="content-wrapper">
            <div class="container-xxl flex-grow-1 container-p-y">
              {rightsService("HAC057") && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{ marginBottom: 16 }}
                  onClick={() => {
                    navigate("/ajouter-profil");
                  }}
                >
                  Créer un nouveau profil
                </Button>
              )}
              <div class="card">
                <ExportableFilterableTable
                  columns={columns}
                  dataSource={profils}
                  exportDataSource={allProfils}
                  onChange={handleChange}
                  rowKey="codeId"
                  pagination={pagination}
                  loading={loading}
                  exportFileName="profils"
                  expandable={{
                    expandedRowRender: (record) => (
                      <p style={{ margin: 0 }}>{record.commentaire}</p>
                    ),
                  }}
                />
              </div>
              <Footer />
            </div>
          </div>
          {/* <!-- Content wrapper --> */}
        </div>
        {/* <!-- / Layout page --> */}
      </div>
      {/* <!-- Overlay --> */}
      <div class="layout-overlay layout-menu-toggle"></div>

      {/* <!-- Drag Target Area To SlideIn Menu On Small Screens --> */}
      <div class="drag-target"></div>
    </div>
  );
};

export default Profils;
